import { Component } from '@theme/component';

/** Maps original photo points through cover cropping and connects them to responsive product links. */
class GPLookbookPhoto extends Component {
  #observer;
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('load', this.#schedule, true);
    this.#observe();
    document.fonts.ready.then(() => {
      if (this.isConnected) this.#schedule();
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('load', this.#schedule, true);
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #observe() {
    this.#observer?.disconnect();
    this.#observer = new ResizeObserver(this.#schedule);
    this.#observer.observe(this);
    this.querySelectorAll('[data-look-label]').forEach((label) => this.#observer.observe(label));
    this.#schedule();
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#position());
  };

  #position() {
    const image = this.querySelector('img');
    const width = this.clientWidth;
    const height = this.clientHeight;
    if (!image || !width || !height) return;

    const imageWidth = Number(image.getAttribute('width')) || image.naturalWidth;
    const imageHeight = Number(image.getAttribute('height')) || image.naturalHeight;
    if (!imageWidth || !imageHeight) return;

    const scale = Math.max(width / imageWidth, height / imageHeight);
    const drawnWidth = imageWidth * scale;
    const drawnHeight = imageHeight * scale;
    const position = getComputedStyle(image).objectPosition.split(' ').map((value) => parseFloat(value) / 100);
    const cropX = (width - drawnWidth) * position[0];
    const cropY = (height - drawnHeight) * position[1];

    for (const hotspot of this.querySelectorAll('[data-look-hotspot]')) {
      const label = hotspot.querySelector('[data-look-label]');
      const line = hotspot.querySelector('[data-look-line]');
      const circle = hotspot.querySelector('[data-look-point]');
      const itemX = drawnWidth * Number(hotspot.dataset.itemX) / 100 + cropX;
      const itemY = drawnHeight * Number(hotspot.dataset.itemY) / 100 + cropY;
      const styles = getComputedStyle(hotspot);
      const labelStyles = getComputedStyle(label);
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const halfWidth = parseFloat(labelStyles.width) / 2;
      const halfHeight = parseFloat(labelStyles.height) / 2;
      const labelX = Math.max(halfWidth + 12, Math.min(width - halfWidth - 12,
        width * parseFloat(styles.getPropertyValue(mobile ? '--mobile-x' : '--label-x')) / 100));
      const labelY = Math.max(halfHeight + 12, Math.min(height - halfHeight - 12,
        height * parseFloat(styles.getPropertyValue(mobile ? '--mobile-y' : '--label-y')) / 100));

      label.style.left = `${labelX}px`;
      label.style.top = `${labelY}px`;

      const dx = itemX - labelX;
      const dy = itemY - labelY;
      const edge = 1 / Math.max(Math.abs(dx) / halfWidth, Math.abs(dy) / halfHeight);
      const visible = itemX >= 4 && itemX <= width - 4 && itemY >= 4 && itemY <= height - 4 && edge < 1;
      hotspot.querySelector('svg').style.display = visible ? '' : 'none';
      if (!visible) continue;

      const borderX = halfWidth - parseFloat(labelStyles.borderLeftWidth) / 2;
      const borderY = halfHeight - parseFloat(labelStyles.borderTopWidth) / 2;
      const radius = Math.min(parseFloat(labelStyles.borderTopLeftRadius), borderX, borderY);
      let startX;
      let startY;
      if (Math.abs(dx) / borderX > Math.abs(dy) / borderY) {
        startX = labelX + Math.sign(dx) * borderX;
        startY = labelY + Math.max(-borderY + radius, Math.min(borderY - radius, dy * borderX / Math.abs(dx)));
      } else {
        startX = labelX + Math.max(-borderX + radius, Math.min(borderX - radius, dx * borderY / Math.abs(dy)));
        startY = labelY + Math.sign(dy) * borderY;
      }
      const itemDx = itemX - startX;
      const itemDy = itemY - startY;
      const distance = Math.hypot(itemDx, itemDy);
      line.setAttribute('x1', startX);
      line.setAttribute('y1', startY);
      line.setAttribute('x2', itemX - itemDx / distance * 4);
      line.setAttribute('y2', itemY - itemDy / distance * 4);
      circle.setAttribute('cx', itemX);
      circle.setAttribute('cy', itemY);
    }

    this.dataset.positioned = '';
  }
}

if (!customElements.get('gp-lookbook-photo')) {
  customElements.define('gp-lookbook-photo', GPLookbookPhoto);
}
